import { useCallback } from "react";
import { useAppContext } from "../../../../context/AppContext";
import { getCountryPhoneLength } from "../../../../utils/countryPhoneLengths";

/**
 * Custom hook for form validation functions
 * Provides validation functions for email, password, phone number, and required fields
 */
export const useFormValidation = () => {
  const { getTranslation } = useAppContext();

  const validateEmail = useCallback(
    (email: string): string | null => {
      if (!email.trim()) {
        return getTranslation("Txt7");
      }
      if (!/^\S+@\S+\.\S+$/.test(email)) {
        return getTranslation("Txt713");
      }
      // Check if email contains letters (not just numbers)
      if (!/[a-zA-Z]/i.test(email)) {
        return getTranslation("Txt713");
      }
      return null;
    },
    [getTranslation]
  );

  // Basic password validation for signup
  const validatePassword = useCallback(
    (password: string): string | null => {
      if (!password) {
        return getTranslation("Err22");
      }
      if (password.length < 6) {
        return getTranslation("Err24");
      }
      return null;
    },
    [getTranslation]
  );

  // Strong password validation with complexity requirements
  const validateStrongPassword = useCallback(
    (password: string): string | null => {
      if (!password) {
        return getTranslation("Err22");
      }
      if (password.length < 6) {
        return getTranslation("Err24");
      }
      
      // // Check for uppercase, lowercase, number, and special character
      // const hasUppercase = /[A-Z]/.test(password);
      // const hasLowercase = /[a-z]/.test(password);
      // const hasNumber = /\d/.test(password);
      // const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
      
      // if (!hasUppercase || !hasLowercase || !hasNumber || !hasSpecialChar) {
      //   return getTranslation("Err25");
      // }
      
      return null;
    },
    [getTranslation]
  );

  // Password confirmation validation
  const validatePasswordConfirmation = useCallback(
    (password: string, confirmPassword: string): string | null => {
      if (!confirmPassword) {
        return getTranslation("Err22");
      }
      if (password !== confirmPassword) {
        return getTranslation("Err26");
      }
      return null;
    },
    [getTranslation]
  );

  // Login password validation (basic check)
  const validateLoginPassword = useCallback(
    (password: string): string | null => {
      if (!password) {
        return getTranslation("Err22");
      }
      return null;
    },
    [getTranslation]
  );

  const validateRequired = useCallback(
    (value: string): string | null => {
      if (!value.trim()) {
        return getTranslation("Txt6");
      }
      return null;
    },
    [getTranslation]
  );

  const validateRequiredfirst = useCallback(
    (value: string): string | null => {
      if (!value.trim()) {
        return getTranslation("Txt709");
      }
      return null;
    },
    [getTranslation]
  );

  const validateRequiredlast = useCallback(
    (value: string): string | null => {
      if (!value.trim()) {
        return getTranslation("Txt711");
      }
      return null;
    },
    [getTranslation]
  );

  const validateRequiredcountry = useCallback(
    (value: string): string | null => {
      if (!value.trim()) {
        return getTranslation("Txt894") + "*"
      }
      return null;
    },
    [getTranslation]
  );

  const validatePhoneNumber = useCallback(
    (value: string, countryCode?: string): string | null => {
      const trimmed = value.trim();
      if (!trimmed) {
        return getTranslation("Txt949");
      }
      const normalized = trimmed.replace(/\D/g, "");
      
      const phoneLength = getCountryPhoneLength(countryCode);
      if (phoneLength) {
        const { min, max } = phoneLength;
        if (normalized.length < min || normalized.length > max) {
            return `${getTranslation("Txt950")} ${min}${min !== max ? `-${max}` : ''} ${getTranslation("Txt951")}`;
          }
      } else {
        // Default validation: 10 digits for any other country
        if (normalized.length !== 10) {
          return `${getTranslation("Txt950")} 10 ${getTranslation("Txt951")}`;
        }
      }
      return null;
    },
    [getTranslation]
  );

  return { 
    validateEmail, 
    validatePassword, 
    validateStrongPassword, 
    validatePasswordConfirmation, 
    validateLoginPassword, 
    validateRequired,
    validateRequiredfirst,
    validateRequiredlast,
    validateRequiredcountry,
    validatePhoneNumber
  };
};
